import { useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback } from "react";
import { logger } from "../../../datadog/logger";
import { useConnectRoomCallback } from "../../../session/useConnectRoomCallback";
import { toast } from "../../../toasts";
import { GenericRoomError } from "../../../video-sdk/core/utils";
import { NEW_MESSAGE_TOAST_TAG } from "../../chat/useShowNewMessageToastCallback";
import { setDefaultMeetingRoomFaviconAndTitle } from "../../meeting-room/metadata";
import {
  activeConversationSidAtom,
  preSessionVibesOpenAtom,
  visitorNameAtom,
  VisitorState,
  visitorStateAtom,
} from "../../state";
import {
  deadlineAtom,
  sessionConversationSidAtom,
} from "../delayed-entry/state";
import { usePreSessionPromptState } from "../pre-session-prompt/usePreSessionPromptState";
import { joinAttemptsAtom } from "./state";
import { useClearAdmissionStateCallback } from "./useClearAdmissionState";
import { useAdmissionFlowCallback } from "../../../hooks/useAdmissionFlow";
import { DebugMutex } from "../../../video-sdk/core/mutex";
import { RoomValidationError } from "../../../video-sdk/core/room/validators/RoomValidationError";
import { validateRoomHasParticipants } from "../../../video-sdk/core/room/validators";

const showErrorToast = (message: string) => {
  toast.error(message, { purpose: "system" });
};

const connectRoomMutex = new DebugMutex("connectRoomMutex");
/**
 * Visitor-specific room connection with error handling and cleanup
 */
export function useVisitorConnectRoomCallback() {
  const [connectRoom] = useConnectRoomCallback();
  const setVisitorState = useSetAtom(visitorStateAtom);
  const getVisitorName = useAtomCallback(
    useCallback((get) => get(visitorNameAtom), []),
  );
  const setActiveConversationSid = useSetAtom(activeConversationSidAtom);
  const setDeadline = useSetAtom(deadlineAtom);
  const setPresessionVibesOpen = useSetAtom(preSessionVibesOpenAtom);
  const setJoinAttempts = useSetAtom(joinAttemptsAtom);
  const { clearPreSessionPrompt } = usePreSessionPromptState();
  const getSessionConversationSid = useAtomCallback(
    useCallback((get) => get(sessionConversationSidAtom), []),
  );
  const clearAdmissionState = useClearAdmissionStateCallback();
  const getAdmissionFlow = useAdmissionFlowCallback();

  const visitorConnectRoom = useCallback(async () => {
    if (connectRoomMutex.isLocked()) {
      logger.warn("Failed to join room: connection already in progress");
      return null;
    }

    return connectRoomMutex.runExclusive(async () => {
      setVisitorState(VisitorState.JOINING);
      const conversationSid = getSessionConversationSid();
      setJoinAttempts((count) => count + 1);

      const room = await connectRoom(getVisitorName(), {
        roomValidators: [validateRoomHasParticipants],
        onError: (error) => {
          if (
            error instanceof RoomValidationError &&
            error.kind === "empty-room"
          ) {
            showErrorToast("Your provider may have left the room");
            logger.warn("Room has less than 1 remote participant");
            clearAdmissionState();
            return;
          }

          if (
            getAdmissionFlow() === "manual" &&
            error instanceof GenericRoomError &&
            error.kind === "connection-error"
          ) {
            // We could auto retry here but for consistent session audio/video
            // it's probably safest to let the retry be via a user interaction
            showErrorToast(
              "Failed to connect to the meeting. Please try again.",
            );
            logger.error("Connection failure when joining room", {}, error);
            return;
          }

          showErrorToast("There was an issue joining the meeting");
          logger.error("Failed to join room", {}, error as Error);
          clearAdmissionState();
        },
      });

      if (!room) {
        setVisitorState(VisitorState.WAITING);
        return null;
      }

      setVisitorState(VisitorState.MEETING);
      setDeadline(null);
      setPresessionVibesOpen(false);
      if (conversationSid) {
        toast.closeByTag(NEW_MESSAGE_TOAST_TAG);
        setActiveConversationSid(conversationSid);
      }

      clearPreSessionPrompt();
      setDefaultMeetingRoomFaviconAndTitle();

      return room;
    }, "useVisitorConnectRoomCallback");
  }, [
    getSessionConversationSid,
    setJoinAttempts,
    connectRoom,
    getVisitorName,
    setVisitorState,
    setDeadline,
    setPresessionVibesOpen,
    clearPreSessionPrompt,
    getAdmissionFlow,
    clearAdmissionState,
    setActiveConversationSid,
  ]);

  return visitorConnectRoom;
}
